/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useEffect, useCallback, memo } from 'react';
import UserContext from './UserContext';
import initialUserState from './initialUserState';
import userReducer from 'contexts/user/reducer/user.reducer';
import * as action from 'contexts/user/reducer/user.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { IselectUserSuperAdminProps, TUpdateUserPick } from 'interfaces/user.interface';
import { IChildrenProps } from 'interfaces/general.interface';
import useContextGeneral from 'hooks/contexts/useContextGeneral';

function UserProvider(props: IChildrenProps) {
  const [userState, dispatch] = useReducer(userReducer, initialUserState);
  const { user, isAuthenticated, isLoading, getAccessTokenSilently, logout } = useAuth0();
  const { t: translate } = useTranslation();
  const { ping } = useContextGeneral();

  useEffect(() => {
    if ((isLoading && !isAuthenticated) || !ping) return;
    getAccessTokenSilently().then(async (token: string) => {
      if (!token) return;
      return await action.loginUserAction({
        dispatch,
        token,
        emailVerified: user?.email_verified,
        translate,
        logout,
      });
    });
  }, [isLoading, isAuthenticated, user?.email_verified, ping]);

  const updateUser = useCallback(
    async ({ user }: { user: TUpdateUserPick }) => {
      if (!userState.dbUser?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateUserAction({
        dispatch,
        dbUserId: userState.dbUser.id,
        data: user,
        token,
        translate,
      });
    },
    [userState.dbUser?.id]
  );

  const updateUserAvatar = useCallback(
    async (image: File) => {
      if (!userState.dbUser?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateUserAvatarAction({
        dispatch,
        userId: userState.dbUser.id,
        image,
        token,
        translate,
      });
    },
    [userState.dbUser?.id]
  );

  const updateLanguage = useCallback(
    async ({ languageId }: { languageId: string }) => {
      if (!userState.dbUser?.id || languageId === userState.dbUser.i18n_lang_id)
        return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateLanguageAction({
        dispatch,
        dbUserId: userState.dbUser.id,
        languageId,
        token,
        translate,
      });
    },
    [userState.dbUser?.id]
  );

  const selectUserSuperAdmin = useCallback(({ user }: IselectUserSuperAdminProps) => {
    return action.selectUserSuperAdminAction({
      dispatch,
      user,
    });
  }, []);

  const memoProvider = useMemo(
    () => ({
      ...userState,
      updateUser,
      updateUserAvatar,
      updateLanguage,
      selectUserSuperAdmin,
    }),
    [userState, updateUser, updateUserAvatar, updateLanguage, selectUserSuperAdmin]
  );

  return (
    <UserContext.Provider value={memoProvider}>{props.children}</UserContext.Provider>
  );
}

export default memo(UserProvider);
